// TODO: Split this file in separated by domain files

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  CardContent as MUICardContent,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import styled from 'styled-components'
import { Formik, Form, Field } from 'formik'
import {
  ComposedChart,
  XAxis,
  YAxis,
  Bar,
  Line,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import IconCard from './components/IconCard'
import BestParams from './components/BestParams'
import DataVariationIndicator from './components/DataVariationIndicator'
import { BREAKPOINTS } from './config/breakpoints'
import { useCountries } from './service/hooks/useCountries'
import { useCountryData } from './service/hooks/useCountryData'
import { useCountryParams } from './service/hooks/useCountryParams'
import { CountryData } from './service/types/countries'
import { usePrediction } from './service/hooks/usePrediction'
import theme from './config/theme'
import WithLoading from './components/WithLoading'
// import { getFutureDate } from './utils/date'
import { getHeigherValue } from './utils/dataFormatter'

require('dotenv').config()

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background-color: ${({ theme }) => theme.palette.background.default};
`

const PageContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1300px;
`

const CardContent = styled(MUICardContent)`
  width: 100%;
  height: 100%;
`

const SelectContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (min-width: ${BREAKPOINTS.SMALL}px) {
    justify-content: flex-start;
  }
`

const StyledSelect = styled(Select)`
  width: 72%;
  margin-right: 20px;

  @media (min-width: ${BREAKPOINTS.SMALL}px) {
    width: 360px;
  }
`

const ChartSection = styled.div<{ bottom?: boolean }>`
  width: 100%;
  display: flex;
  flex: 1;
  margin-top: ${({ bottom }) => (bottom ? -40 : -10)}px;
  padding-left: 20px;
  overflow: visible;

  @media (min-width: ${BREAKPOINTS.SMALL}px) {
    margin-top: 0px;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: 20px !important;
  margin-bottom: 20px !important;
  height: 55px;

  @media (min-width: ${BREAKPOINTS.MEDIUM}px) {
    width: 160px;
    margin-left: 20px !important;
  }
`

const StyledInput = styled(TextField)`
  width: 20%;
  max-width: 80px;
  margin-right: 20px;
`

const GridSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
`

const CardContainer = styled(Card)`
  display: flex;
  min-height: 140px;
  width: 100%;
  margin-bottom: 20px;
  border-radius: 14px;

  @media (min-width: ${BREAKPOINTS.SMALL}px) {
    width: calc(50% - 10px);
    height: 10.5vh;
  }

  @media (min-width: ${BREAKPOINTS.LARGE}px) {
    width: calc(25% - 10px);
  }
`

const Content = styled(CardContent)`
  width: 100%;
`

const ChartContainer = styled(Card)`
  width: 100%;
  height: 50vh;
  margin-bottom: 20px;
  border-radius: 14px;

  @media (min-width: ${BREAKPOINTS.LARGE}px) {
    width: calc(50% - 10px);
  }
`

const ChartWrapper = styled.div`
  width: 90%;
  height: 90%;

  margin-top: 25px;
  display: flex;
  flex-direction: column;
`

interface CountryFormProps {
  country: string
  days: string
}

const formatCountryCases = (country?: CountryData) => {
  if (!country) {
    return null
  }

  const totalCases = country.total_cases
  const totalDeaths = country.total_deaths

  return {
    total: {
      current: totalCases[totalCases.length - 1],
      previous: totalCases[totalCases.length - 2],
    },
    deaths: {
      current: totalDeaths[totalDeaths.length - 1],
      previous: totalDeaths[totalDeaths.length - 2],
    },
    daily: {
      current:
        totalDeaths[totalDeaths.length - 1] -
        totalDeaths[totalDeaths.length - 2],
      previous:
        totalDeaths[totalDeaths.length - 2] -
        totalDeaths[totalDeaths.length - 3],
    },
  }
}

const INDIVIDUAL_WAVE_COLORS = [
  '#d25656',
  '#5b8c4f',
  '#5671d2',
  '#d28a56',
  '#56d279',
  '#7956d2',
  '#56b9d2',
  '#cad256',
  '#4777ad',
  '#7f47ad',
  '#d30656',
  '#530c4f',
  '#5301d2',
  '#134a56',
  '#512279',
  '#7996d2',
  '#1442d2',
  '#fff256',
  '#f777ad',
  '#eee7ad',
]

function App() {
  const { data, isLoading, isError, error } = useCountries()
  const placeholder = isLoading ? ['Brazil'] : []
  const countries = [...placeholder, ...(data || [])]
  const [selectedCountry, setSelectedCountry] = useState('Brazil')
  const [isLoadingPredictions, setLoadingPredictions] = useState(false)
  const [predictions, setPredictions] = useState<any>(null)
  const [currentDays, setCurrentDays] = useState(10)

  const { data: params, isLoading: isLoadingParams } =
    useCountryParams(selectedCountry)

  const { data: countryCases, isLoading: isLoadingData } =
    useCountryData(selectedCountry)

  const { mutateAsync } = usePrediction()

  const formattedCountry = formatCountryCases(countryCases)

  // const futureDate = getFutureDate(currentDays - 1)
  //   .toUTCString()
  //   .split(',')[1]
  //   .slice(0, 12)

  const futureValueIndex =
    params &&
    parseInt((currentDays / params.chunck_size).toFixed(0)) +
      predictions?.merged?.data?.length

  const dailyPredictedValue = predictions?.filled?.[futureValueIndex - 2] || {
    wave_0: 0,
    bar: 0,
  }

  const dailyPrediction = getHeigherValue(dailyPredictedValue)

  const accumulatedPrediction =
    predictions?.merged?.data[predictions?.merged?.data?.length! - 1]
      ?.prediction || '?'

  const handleSuccess = useCallback((result: any) => {
    setLoadingPredictions(false)
    setPredictions(result)
  }, [])

  const handleError = useCallback(() => {
    setLoadingPredictions(false)
    setPredictions(null)
  }, [])

  const handleSubmit = useCallback(
    async (values: CountryFormProps) => {
      setSelectedCountry(values.country)
      setLoadingPredictions(true)
      setCurrentDays(parseInt(values.days))
      mutateAsync(
        {
          countryName: values.country,
          daysToPredict: values.days,
          metric: 'total_cases',
        },
        {
          onSuccess: handleSuccess,
          onError: handleError,
        }
      )
    },
    [setSelectedCountry]
  )

  useEffect(() => {
    handleSubmit({ country: selectedCountry, days: currentDays.toString() })
  }, [])

  if (isError) {
    return <div>{JSON.stringify(error)}</div>
  }

  return (
    <Formik
      initialValues={{ country: 'Brazil', days: '10' }}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form>
          <Container>
            <PageContent>
              <GridSection>
                <SelectContainer>
                  <Field
                    as={StyledSelect}
                    name='country'
                    variant='outlined'
                    defaultValue={formikProps.values.country}
                    onChange={(e: any) => {
                      formikProps.handleChange(e)
                    }}
                  >
                    {countries.map((country) => {
                      return (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      )
                    })}
                  </Field>
                  <Field
                    as={StyledInput}
                    name='days'
                    id='outlined-basic'
                    label='Chunks'
                    variant='outlined'
                    type='number'
                  />
                  <StyledButton
                    color='primary'
                    variant='contained'
                    type='submit'
                  >
                    MAKE PREDICTION
                  </StyledButton>
                </SelectContainer>
              </GridSection>
              <GridSection>
                <CardContainer>
                  <Content>
                    <WithLoading loading={isLoadingParams}>
                      <IconCard
                        info='Country'
                        value={params?.country_name!}
                        bottomComponent={
                          <BestParams
                            chunckSize={params?.chunck_size!}
                            waveOffset={params?.wave_offset!}
                            movingAverageIndex={params?.moving_average_index!}
                          />
                        }
                      />
                    </WithLoading>
                  </Content>
                </CardContainer>
                <CardContainer>
                  <CardContent>
                    <WithLoading loading={isLoadingData}>
                      <IconCard
                        info={'Total cases'}
                        value={`${formattedCountry?.total.current}`}
                        bottomComponent={
                          <DataVariationIndicator
                            previousValue={formattedCountry?.total.previous!}
                            currentValue={formattedCountry?.total.current!}
                            period='On the last 24 hours'
                          />
                        }
                      />
                    </WithLoading>
                  </CardContent>
                </CardContainer>
                <CardContainer>
                  <CardContent>
                    <WithLoading loading={isLoadingData}>
                      <IconCard
                        info={'Total deaths'}
                        value={`${formattedCountry?.deaths.current}`}
                        bottomComponent={
                          <DataVariationIndicator
                            previousValue={formattedCountry?.deaths?.previous!}
                            currentValue={formattedCountry?.deaths?.current!}
                            period='On the last 24 hours'
                          />
                        }
                      />
                    </WithLoading>
                  </CardContent>
                </CardContainer>
                <CardContainer>
                  <CardContent>
                    <WithLoading loading={isLoadingData}>
                      <IconCard
                        info={'Daily deaths'}
                        value={`${formattedCountry?.daily?.current}`}
                        bottomComponent={
                          <DataVariationIndicator
                            previousValue={formattedCountry?.daily?.previous!}
                            currentValue={formattedCountry?.daily?.current!}
                            period='On the last 24 hours'
                          />
                        }
                      />
                    </WithLoading>
                  </CardContent>
                </CardContainer>
              </GridSection>
              <GridSection>
                <ChartContainer>
                  <WithLoading loading={isLoadingPredictions}>
                    {predictions?.merged?.data && (
                      <ChartWrapper>
                        <ChartSection>
                          <Typography>
                            Accumulated cases (R² ={' '}
                            {predictions?.merged?.r_squared})
                          </Typography>
                        </ChartSection>
                        <ResponsiveContainer>
                          <ComposedChart
                            margin={{
                              top: 5,
                              right: 20,
                              left: 20,
                              bottom: 45,
                            }}
                            data={predictions?.merged?.data || []}
                          >
                            <XAxis fontSize={10} fontFamily='Roboto' />
                            <YAxis fontSize={10} fontFamily='Roboto' />
                            <Tooltip
                              labelStyle={{ fontFamily: 'Roboto' }}
                              itemStyle={{ fontFamily: 'Roboto' }}
                              cursor={{
                                fill: `${theme.palette.text.secondary}`,
                                width: 20,
                              }}
                            />
                            <Bar
                              dataKey='real_data'
                              name='Real Data'
                              barSize={20}
                              fill={`${theme.palette.text.secondary}33`}
                            />
                            <Line
                              type='linear'
                              dataKey='prediction'
                              name='Predicted Data'
                              dot={false}
                              stroke={theme.palette.primary.main}
                              strokeWidth={3}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                        <ChartSection bottom>
                          <div style={{ display: 'flex' }}>
                            <Typography>Prediction: </Typography>
                            <Typography
                              color='primary'
                              style={{ marginRight: 4, marginLeft: 4 }}
                            >
                              {accumulatedPrediction}
                            </Typography>
                            <Typography>cases</Typography>
                          </div>
                        </ChartSection>
                      </ChartWrapper>
                    )}
                  </WithLoading>
                </ChartContainer>
                <ChartContainer>
                  <WithLoading loading={isLoadingPredictions}>
                    {predictions?.filled && (
                      <ChartWrapper>
                        <ChartSection>
                          <Typography>Daily cases</Typography>
                        </ChartSection>
                        <ResponsiveContainer>
                          <ComposedChart
                            data={predictions.filled.slice(
                              0,
                              futureValueIndex - 1
                            )}
                            margin={{
                              top: 5,
                              right: 20,
                              left: 20,
                              bottom: 45,
                            }}
                          >
                            <XAxis fontSize={10} fontFamily='Roboto' />
                            <YAxis fontSize={10} fontFamily='Roboto' />
                            <Tooltip
                              labelStyle={{ fontFamily: 'Roboto' }}
                              itemStyle={{ fontFamily: 'Roboto' }}
                              cursor={{
                                fill: `${theme.palette.text.secondary}`,
                                width: 20,
                              }}
                            />
                            <Bar
                              dataKey='bar'
                              name='Real Data'
                              barSize={20}
                              fill={`${theme.palette.text.secondary}22`}
                            />
                            {Object.keys(predictions.filled[0]).map((key) => {
                              const waveIndex = key.split('_')[1]
                              return (
                                key.includes('wave') && (
                                  <Line
                                    key={key}
                                    type='linear'
                                    name={`Wave ${waveIndex}`}
                                    dataKey={key}
                                    dot={false}
                                    strokeWidth={2}
                                    stroke={
                                      INDIVIDUAL_WAVE_COLORS[
                                        parseInt(waveIndex) - 1
                                      ]
                                    }
                                  />
                                )
                              )
                            })}
                          </ComposedChart>
                        </ResponsiveContainer>
                        <ChartSection bottom>
                          <div style={{ display: 'flex' }}>
                            <Typography>Prediction: </Typography>
                            <Typography
                              color='primary'
                              style={{ marginRight: 4, marginLeft: 4 }}
                            >
                              {dailyPrediction}
                            </Typography>
                            <Typography>cases</Typography>
                          </div>
                        </ChartSection>
                      </ChartWrapper>
                    )}
                  </WithLoading>
                </ChartContainer>
                {!isLoadingPredictions &&
                  predictions?.individual?.data &&
                  (predictions?.individual?.data as any[][]).map(
                    (wave, index) => {
                      return (
                        <ChartContainer key={`${index}`}>
                          <ChartWrapper>
                            <ChartSection>
                              <Typography>
                                Wave {index + 1} (R² ={' '}
                                {predictions?.individual?.r_squared[index] ||
                                  '?'}
                                )
                              </Typography>
                            </ChartSection>
                            <ResponsiveContainer>
                              <ComposedChart
                                margin={{
                                  top: 5,
                                  right: 20,
                                  left: 20,
                                  bottom: 45,
                                }}
                                data={wave || []}
                              >
                                <XAxis fontSize={10} fontFamily='Roboto' />
                                <YAxis fontSize={10} fontFamily='Roboto' />
                                <Tooltip
                                  labelStyle={{ fontFamily: 'Roboto' }}
                                  itemStyle={{ fontFamily: 'Roboto' }}
                                  cursor={{
                                    fill: `${theme.palette.text.secondary}`,
                                    width: 20,
                                  }}
                                />
                                <Bar
                                  dataKey='real_data'
                                  name='Real Data'
                                  barSize={20}
                                  fill={`${theme.palette.text.secondary}33`}
                                />
                                <Line
                                  type='linear'
                                  dataKey='prediction'
                                  name='Predicted Data'
                                  dot={false}
                                  stroke={INDIVIDUAL_WAVE_COLORS[index]}
                                  strokeWidth={3}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </ChartWrapper>
                        </ChartContainer>
                      )
                    }
                  )}
              </GridSection>
            </PageContent>
          </Container>
        </Form>
      )}
    </Formik>
  )
}

export default App
